import { useState } from "react";

// A self-contained prompt a developer can paste into Cursor / Claude / ChatGPT
// to wire notify into their app. Kept framework-agnostic but explicit about the
// publishable/secret key split so the AI never leaks the secret to the browser.
const PROMPT = `Integrate "notify" (https://getnotify.dev) — a hosted browser push notification service — into my web app. Detect my framework and use its idioms.

How notify works:
- A project has two keys: a PUBLISHABLE key (ntfy_pk_...) used in the browser, and a SECRET key (ntfy_sk_...) used only on my server to send. Never put the secret key in client code or a public env var.
- Base URL: https://api.getnotify.dev

Implement these steps, adapted to my stack:

1. Service worker — create a file served at the site root /notify-sw.js (same-origin) containing exactly:
   importScripts("https://api.getnotify.dev/sw.js");

2. Load the client script on every page using my PUBLISHABLE key (read from a public env var):
   <script src="https://api.getnotify.dev/notify.js?token=PUBLISHABLE_KEY"></script>
   It exposes a global window.notify.

3. Add an "Enable notifications" button that, after the user opts in, calls:
   await window.notify.subscribe(USER_ID)
   USER_ID = a stable, pseudonymous id for the signed-in user (not an email).

4. Server-side send — POST https://api.getnotify.dev/send with my SECRET key (from a server-only env var):
   { "token": "SECRET_KEY", "userId": "USER_ID", "title": "My App", "body": "Your message" }
   Returns 200 on success, 404 if that user has no subscription.

Requirements:
- Put PUBLISHABLE key in a public env var, SECRET key in a server-only env var. Read both from env; do not hardcode.
- Use framework-native patterns (e.g. next/script + a server action/route handler for Next.js; root-route head + a server function for TanStack Start).
- Wire a minimal "Enable notifications" UI to window.notify.subscribe and a server function/endpoint that sends.
- Tell me exactly where to set the two env vars.

Get keys by running: npx notify-dev init
My publishable key: <paste ntfy_pk_... or leave blank and ask me>
My secret key: <paste ntfy_sk_... — server only>

If you can't detect my framework, ask before writing code.`;

export function AiPromptButton() {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={"ai-btn" + (copied ? " on" : "")}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(PROMPT);
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {copied ? "✓ Copied — paste into your AI assistant" : "Copy AI integration prompt"}
    </button>
  );
}
